import { useApolloClient } from '@apollo/client';
import { Box } from '@mui/material';
import { difference } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { DeleteButton } from '@/components/form/baseInputs/Button/DeleteButton';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { Modal } from '@/components/modals/Modal/Modal';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Loader } from '@/components/progress/Loader/Loader';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { shouldRefetchQuery } from '@/graphql/client.utils';
import { useGraphQLErrors, useSpecificGraphQLError } from '@/graphql/errors';
import { useRecordNotFoundHandler } from '@/hooks/useRecordNotFoundHandler';
import { useReportError } from '@/hooks/useReportError';
import { GetUserStateDocument } from '@/modules/authentication/graphql/Authentication.generated';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { AUTHZED_RELATIONS } from '@/modules/authentication/rolesConstants';
import { useEmployeeIdpIdEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useEmployeeIdpIdEnabled';
// eslint-disable-next-line luminary-rules/no-page-imports
import { GetAdminUsersDocument } from '@/pages/admin/AdminUsersPage/graphql/AdminUsers.generated';
import { EmployeeTier, ErrorCodes } from '@/types/schema';
import * as diagnostics from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import {
  ClientReassignmentModal,
  ClientReassignmentModalCloseReason,
} from './ClientReassignmentModal';
import { EmployeeForm } from './EmployeeForm';
import { useCreateEmployeeMutation } from './graphql/CreateEmployee.generated';
import { useDeleteUserMutation } from './graphql/DeleteEmployee.generated';
import { useGetEmployeeQuery } from './graphql/GetEmployee.generated';
import { useResendInviteMutation } from './graphql/ResendInvite.generated';
import { useUpdateEmployeeMutation } from './graphql/UpdateEmployee.generated';
import {
  dataToForm,
  getCreateInput,
  getMutationInput,
  useGetEmployeeRoleOptions,
} from './utils';

const defaultValues = {
  employeeId: '',
  userId: '',
  firstName: '',
  lastName: '',
  email: '',
  roleIds: [],
  tier: EmployeeTier.Advanced,
  teamIds: [],
  clientIds: [],
  idpId: '',
};

interface Props {
  isOpen: boolean;
  employeeId: string | null;
  closeHandler: () => void;
}

export interface EmployeeFormShape {
  employeeId: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  roleIds: string[];
  isAdmin: boolean;
  tier: EmployeeTier;
  teamIds: string[];
  clientIds: string[];
  idpId: string;
}

export function EmployeeModal({ isOpen, employeeId, closeHandler }: Props) {
  const isCreatingUser = employeeId === null;
  const currentUser = useCurrentUser();
  // The IdP ID field is shown only when the tenant has enabled it via
  // Settings → Internal → Configurations.
  const showIdpId = useEmployeeIdpIdEnabled();
  const apolloClient = useApolloClient();
  const { showFeedback, createErrorFeedback } = useFeedback();
  const canViewHouseholds = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_VIEW_HOUSEHOLDS,
    { detailed: false }
  );
  const [isClientReassignmentModalOpen, setIsClientReassignmentModalOpen] =
    useState(false);
  const [householdsRequiringReassignment, setHouseholdsRequiringReassignment] =
    useState<Array<{ id: string; displayName: string }>>([]);
  const [pendingAdminRemovalFormValues, setPendingAdminRemovalFormValues] =
    useState<EmployeeFormShape | null>(null);
  const formMethods = useForm<EmployeeFormShape>({
    defaultValues,
  });

  const { control, handleSubmit, reset, getValues } = formMethods;
  const { recoverMissingRecord } = useRecordNotFoundHandler({
    message: 'This user is no longer available.',
    onNotFound: closeHandler,
  });

  const {
    data,
    loading,
    error: queryError,
  } = useGetEmployeeQuery({
    variables: {
      where: {
        id: employeeId,
      },
    },
    skip: !employeeId || isCreatingUser,
    onError: createErrorFeedback(
      "We weren't able to load this user. Please refresh the page and try again."
    ),
  });

  const { roles } = useGetEmployeeRoleOptions();

  const employee = getNodes(data?.employees)[0];
  const assignedHouseholds = useMemo(() => {
    // Filter out any "empty" households that haven't gotten picked up for deletion yet.
    return (employee?.assignedHouseholds ?? []).filter((hh) => hh.displayName);
  }, [employee?.assignedHouseholds]);

  const [resendInvite, { loading: isResending }] = useResendInviteMutation();

  const [deleteUser, { loading: isDeleting }] = useDeleteUserMutation();

  const [updateEmployee, { loading: isUpdating }] = useUpdateEmployeeMutation();

  const [createEmployee, { loading: isCreating, error: createError }] =
    useCreateEmployeeMutation({
      errorPolicy: 'all',
      refetchQueries: [GetAdminUsersDocument],
      awaitRefetchQueries: true,
    });

  const displayErrors = useGraphQLErrors(createError);
  const userAlreadyExists = useSpecificGraphQLError(
    displayErrors,
    ErrorCodes.UserAlreadyExists
  );

  useEffect(() => {
    if (userAlreadyExists) {
      showFeedback(userAlreadyExists.message);
    } else if (createError) {
      showFeedback("We weren't able to create this user. Please try again.");
    }
  }, [createError, showFeedback, userAlreadyExists]);

  const isSubmitting = isUpdating || isResending || isDeleting || isCreating;

  useEffect(
    function emptyFormIfCreating() {
      if (isCreatingUser) {
        reset(defaultValues);
      }
    },
    [isCreatingUser, reset]
  );

  useEffect(
    function initializeForm() {
      const formData = dataToForm(data);

      if (formData) {
        reset(formData);
      } else if (data && !isCreatingUser) {
        recoverMissingRecord();
      }
    },
    [data, isCreatingUser, recoverMissingRecord, reset]
  );

  useReportError('failed to fetch employee', queryError, {
    employeeId,
  });

  const handleClose = useCallback(() => {
    setTimeout(
      () => {
        if (isCreatingUser) {
          reset(defaultValues);
        }
      },
      250,
      isCreatingUser
    );

    closeHandler();
  }, [closeHandler, reset, isCreatingUser]);

  const selectedRolesGrantEmployeeAdmin = (roleIds: string[]) =>
    roles.some(
      (role) =>
        roleIds.includes(role.id) &&
        role.capabilities?.some(
          (capability) =>
            capability.authzedRelationName === AUTHZED_RELATIONS.employees.admin
        )
    );

  const removesOwnEmployeeAdmin = (formValues: EmployeeFormShape) => {
    const originalFormData = dataToForm(data);

    return (
      !isCreatingUser &&
      currentUser?.id === formValues.userId &&
      !!originalFormData &&
      selectedRolesGrantEmployeeAdmin(originalFormData.roleIds) &&
      !selectedRolesGrantEmployeeAdmin(formValues.roleIds)
    );
  };

  const saveEmployee = async (formValues: EmployeeFormShape) => {
    const input = getMutationInput(
      formValues,
      dataToForm(data),
      !!canViewHouseholds,
      showIdpId
    );
    const shouldRefreshUserState = removesOwnEmployeeAdmin(formValues);
    const refetchQueries = shouldRefreshUserState
      ? [GetAdminUsersDocument, GetUserStateDocument]
      : [GetAdminUsersDocument];

    try {
      await updateEmployee({
        variables: {
          input,
        },
        refetchQueries,
        ...(shouldRefreshUserState && {
          awaitRefetchQueries: true,
          // GetUserState is normally session-static. Self-demotion is the
          // exception because authorization must change before this flow ends.
          onQueryUpdated: (query) =>
            shouldRefetchQuery(query.queryName, {
              allowedQueryDocuments: refetchQueries,
            }),
        }),
      });

      showFeedback(`User successfully updated.`, { variant: 'success' });
      handleClose();
    } catch (err) {
      showFeedback("We weren't able to update this user. Please try again.");
      diagnostics.error('Could not update employee', err as Error, {
        userId: input.userID,
      });
    }
  };

  const submitEmployee: SubmitHandler<EmployeeFormShape> = async (
    formValues
  ) => {
    if (isCreatingUser) {
      const input = getCreateInput(formValues, !!canViewHouseholds, showIdpId);

      try {
        const { errors } = await createEmployee({
          variables: {
            input,
          },
        });

        // Check errors due to errorPolicy: 'all'
        if (errors) {
          throw new Error('Could not create employee');
        }

        showFeedback(`An invite has been sent to ${input.create.email}.`, {
          variant: 'success',
        });
        handleClose();
      } catch (err) {
        diagnostics.error('Could not create employee', err as Error, {
          userId: input.create.email,
        });
      }

      // return early to prevent the update mutation from running
      return;
    }

    const originalFormData = dataToForm(data);

    if (originalFormData) {
      const originalClientIds = originalFormData.clientIds || [];
      const currentClientIds = formValues.clientIds || [];
      const clientsToRemove = difference(originalClientIds, currentClientIds);

      if (clientsToRemove.length > 0) {
        const householdsWhereUserIsPrimary =
          employee?.assignedHouseholds?.filter(
            (household) =>
              clientsToRemove.includes(household.id) &&
              household.primaryRelationshipOwner?.id === formValues.userId
          ) || [];

        if (householdsWhereUserIsPrimary.length > 0) {
          setHouseholdsRequiringReassignment(
            householdsWhereUserIsPrimary.map((household) => ({
              id: household.id,
              displayName: household.displayName,
            }))
          );
          setIsClientReassignmentModalOpen(true);
          return;
        }
      }
    }

    await saveEmployee(formValues);
  };

  const submitHandler = handleSubmit(async (formValues) => {
    if (removesOwnEmployeeAdmin(formValues)) {
      setPendingAdminRemovalFormValues(formValues);
      return;
    }

    await submitEmployee(formValues);
  });

  const handleResendInvite = useCallback(async () => {
    const email = getValues('email');

    try {
      await resendInvite({
        variables: {
          email,
        },
      });
      showFeedback(`An invite has been sent to ${email}.`, {
        variant: 'success',
      });
    } catch (err) {
      showFeedback(
        "We weren't able to resend the invite email. Please try again."
      );
      diagnostics.error('Could not resend invite', err as Error, {
        email,
      });
    } finally {
      handleClose();
    }
  }, [getValues, resendInvite, showFeedback, handleClose]);

  // SCIM manages identity fields and deactivation. A later sync can overwrite
  // manual access changes.
  const isSCIMManaged = !!employee?.workosDirectoryUserID;

  const handleDelete = useCallback(async () => {
    // If the user has assigned households, show the reassignment modal first
    if (assignedHouseholds.length > 0) {
      setIsClientReassignmentModalOpen(true);
      return;
    }

    // If no assigned households, proceed with direct deactivation
    const email = getValues('email');

    try {
      await deleteUser({
        variables: {
          email,
        },
      });

      // invalidate the cache for employees
      await apolloClient.refetchQueries({
        updateCache(cache) {
          cache.evict({ fieldName: 'employees' });
        },
      });

      showFeedback('User deactivated successfully.', { variant: 'success' });
    } catch (err) {
      showFeedback(
        "We weren't able to deactivate this user. Please try again."
      );
      diagnostics.error('Could not delete employee', err as Error, {
        email,
      });
    } finally {
      handleClose();
    }
  }, [
    getValues,
    deleteUser,
    apolloClient,
    showFeedback,
    handleClose,
    assignedHouseholds.length,
  ]);

  const actions = useMemo(() => {
    if (isCreatingUser) {
      return (
        <>
          <Button size="sm" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            variant="primary"
            onClick={submitHandler}
            disabled={isSubmitting}
          >
            Add user
          </Button>
        </>
      );
    }

    return (
      <>
        <DeleteButton
          promptDeleteText="Deactivate user"
          onConfirmDelete={handleDelete}
          disabled={isSCIMManaged}
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={handleResendInvite}
          disabled={isSubmitting}
        >
          Resend invite
        </Button>
        <Box flexGrow={1} />
        <Button size="sm" variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          variant="primary"
          onClick={submitHandler}
          loading={isSubmitting}
        >
          Save changes
        </Button>
      </>
    );
  }, [
    handleClose,
    isSubmitting,
    submitHandler,
    handleDelete,
    handleResendInvite,
    isCreatingUser,
    isSCIMManaged,
  ]);

  const handleClientReassignmentModalClose = async (
    reason: ClientReassignmentModalCloseReason,
    reassignToUserId: string | null
  ) => {
    setIsClientReassignmentModalOpen(false);
    setHouseholdsRequiringReassignment([]);

    if (
      reason !== ClientReassignmentModalCloseReason.COMPLETE ||
      !reassignToUserId
    ) {
      return;
    }

    if (householdsRequiringReassignment.length > 0) {
      await saveEmployee(getValues());
      return;
    }

    const email = getValues('email');

    try {
      await deleteUser({
        variables: {
          email,
        },
      });

      await apolloClient.refetchQueries({
        updateCache(cache) {
          cache.evict({ fieldName: 'employees' });
        },
      });

      showFeedback('User deactivated successfully.', {
        variant: 'success',
      });
    } catch (err) {
      showFeedback(
        "We weren't able to deactivate this user. Please try again."
      );
      diagnostics.error('Could not delete employee', err as Error, {
        email,
      });
    } finally {
      handleClose();
    }
  };

  const handleAdminRemovalConfirmationClose = (
    reason: 'confirm' | 'cancel'
  ) => {
    const formValues = pendingAdminRemovalFormValues;
    setPendingAdminRemovalFormValues(null);

    if (reason === 'confirm' && formValues) {
      void submitEmployee(formValues);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        heading={isCreatingUser ? 'Add user' : 'Edit user'}
        onClose={handleClose}
        actions={actions}
      >
        {loading && (
          <Loader
            boxProps={{
              sx: {
                textAlign: 'center',
                my: 3,
              },
            }}
          />
        )}
        {!loading && (employee || isCreatingUser) && (
          <FormProvider {...formMethods}>
            <EmployeeForm
              roles={roles}
              assignedClients={assignedHouseholds}
              control={control}
              isCreatingUser={isCreatingUser}
              isSCIMManaged={isSCIMManaged}
              showIdpId={showIdpId}
            />
          </FormProvider>
        )}
      </Modal>

      <ClientReassignmentModal
        isOpen={isClientReassignmentModalOpen}
        assignedHouseholds={
          householdsRequiringReassignment.length > 0
            ? householdsRequiringReassignment
            : assignedHouseholds
        }
        excludeUserId={getValues('userId')}
        onClose={handleClientReassignmentModalClose}
      />

      {pendingAdminRemovalFormValues && (
        <ConfirmationModal
          isOpen
          heading="Remove your admin access?"
          confirmText="Remove access"
          confirmButtonProps={{ variant: 'destructive-primary' }}
          onClose={handleAdminRemovalConfirmationClose}
        >
          You will lose access to user administration immediately after saving.
          Another administrator may need to restore your access.
        </ConfirmationModal>
      )}
    </>
  );
}
